/** Strength of a hit, 0..1. */
export type HitHandler = (strength: number) => void;

export interface MotionSample {
    /** Acceleration without gravity, m/s². */
    ax: number;
    ay: number;
    az: number;
    /** Gyroscope rotation speed, deg/s. Null where the device has no gyroscope. */
    rotation: number | null;
    /** Milliseconds. */
    t: number;
}

const SWING = 9; // m/s²: the racket stroke
const STOP = 0.3; // the sudden stop has to be at least this fraction of the stroke
const WINDOW_MS = 220; // stroke and stop come this close together
const MAX_ROTATION = 900; // deg/s: more than this is twisting the phone, not a flat stroke
const FULL_STRENGTH = 35;
const COOLDOWN_MS = 350;

/**
 * Recognises a ping-pong stroke: a hard push in one direction, then a sudden stop (acceleration
 * pointing the other way) right after it. A single jolt or a wrist twist does not count. Pure, so it
 * can be tested without a device.
 */
export class RacketHitDetector {
    private stroke: {
        x: number;
        y: number;
        z: number;
        mag: number;
        t: number;
    } | null = null;
    private lastHit = -Infinity;

    /** Returns the hit strength (0..1) when this sample completes a stroke, otherwise null. */
    push(s: MotionSample): number | null {
        const mag = Math.hypot(s.ax, s.ay, s.az);
        if (s.t - this.lastHit < COOLDOWN_MS) return null;
        if (s.rotation !== null && s.rotation > MAX_ROTATION) {
            this.stroke = null;
            return null;
        }
        if (this.stroke && s.t - this.stroke.t > WINDOW_MS) this.stroke = null;

        if (this.stroke) {
            const st = this.stroke;
            const dot = s.ax * st.x + s.ay * st.y + s.az * st.z;
            if (dot < 0 && mag >= st.mag * STOP) {
                this.stroke = null;
                this.lastHit = s.t;
                return Math.min(
                    1,
                    Math.max(0, (st.mag - SWING) / (FULL_STRENGTH - SWING))
                );
            }
        }
        if (mag >= SWING && (!this.stroke || mag > this.stroke.mag)) {
            this.stroke = { x: s.ax, y: s.ay, z: s.az, mag, t: s.t };
        }
        return null;
    }
}

type MotionCtor = typeof DeviceMotionEvent & {
    requestPermission?: () => Promise<'granted' | 'denied'>;
};

/** Listens to the phone's accelerometer and gyroscope and reports racket strokes. */
export class MotionHitDetector {
    private readonly racket = new RacketHitDetector();
    private active = false;

    private readonly onMotion = (e: DeviceMotionEvent) => {
        const a = e.acceleration;
        if (!a || a.x == null || a.y == null || a.z == null) return;
        const r = e.rotationRate;
        const rotation =
            r && r.alpha != null && r.beta != null && r.gamma != null
                ? Math.hypot(r.alpha, r.beta, r.gamma)
                : null;
        const strength = this.racket.push({
            ax: a.x,
            ay: a.y,
            az: a.z,
            rotation,
            t: performance.now(),
        });
        if (strength !== null) this.handler(strength);
    };

    constructor(private readonly handler: HitHandler) {}

    /** iOS asks for permission, and only from a user gesture. Resolves false if unsupported or denied. */
    async start(): Promise<boolean> {
        if (this.active) return true;
        if (typeof window === 'undefined' || !('DeviceMotionEvent' in window))
            return false;
        const ctor = window.DeviceMotionEvent as MotionCtor;
        try {
            if (typeof ctor.requestPermission === 'function') {
                if ((await ctor.requestPermission()) !== 'granted')
                    return false;
            }
        } catch {
            return false;
        }
        window.addEventListener('devicemotion', this.onMotion);
        this.active = true;
        return true;
    }

    stop(): void {
        window.removeEventListener('devicemotion', this.onMotion);
        this.active = false;
    }
}
